import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { getOrder } from '../services/api';

const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

const statusColors = {
  pending:    'bg-yellow-100 text-yellow-700',
  confirmed:  'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped:    'bg-indigo-100 text-indigo-700',
  delivered:  'bg-green-100 text-green-700',
  cancelled:  'bg-red-100 text-red-700',
};

export default function OrderPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOrder(id).then(res => setOrder(res.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-10"><div className="skeleton h-96 rounded-2xl" /></div>;
  if (!order) return null;

  const currentStep = steps.indexOf(order.orderStatus);
  const isCancelled = order.orderStatus === 'cancelled';
  const canReturn   = order.orderStatus === 'delivered';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/orders" className="flex items-center gap-2 text-sm text-gray-400 hover:text-yamass-black mb-8 transition-colors">
        <FiArrowLeft size={16} /> My Orders
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-yamass-black">
            Order #{order._id.slice(-8).toUpperCase()}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Placed {new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <span className={`text-sm font-medium px-4 py-2 rounded-full capitalize ${statusColors[order.orderStatus] || 'bg-gray-100 text-gray-600'}`}>
          {order.orderStatus}
        </span>
      </div>

      {/* Order tracking stepper */}
      {!isCancelled && (
        <div className="bg-yamass-cream rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-yamass-black mb-5">Order Tracking</h2>
          <div className="flex items-center">
            {steps.map((step, i) => (
              <div key={step} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i <= currentStep ? 'bg-yamass-black text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {i < currentStep ? '✓' : i + 1}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 capitalize text-center">{step}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mb-4 mx-1 transition-colors ${i < currentStep ? 'bg-yamass-black' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          {order.trackingNumber && (
            <p className="text-sm text-gray-500 mt-4">Tracking No: <span className="font-medium text-yamass-black">{order.trackingNumber}</span></p>
          )}
        </div>
      )}

      {/* Items */}
      <div className="bg-yamass-cream rounded-2xl p-6 mb-6">
        <h2 className="font-semibold text-yamass-black mb-4">Items Ordered</h2>
        <div className="space-y-4">
          {order.orderItems.map((item, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-white flex-shrink-0">
                {item.image
                  ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-xl">👕</div>
                }
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-yamass-black">{item.name}</p>
                <p className="text-xs text-gray-400">Size: {item.size} · Qty: {item.quantity}</p>
              </div>
              <p className="font-semibold text-yamass-black text-sm">৳{(item.price * item.quantity).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Summary grid */}
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        {/* Shipping address */}
        <div className="bg-yamass-cream rounded-2xl p-5">
          <h3 className="font-semibold text-yamass-black mb-3 text-sm">Shipping Address</h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            {order.shippingAddress.name}<br />
            {order.shippingAddress.line1}<br />
            {order.shippingAddress.line2 && <>{order.shippingAddress.line2}<br /></>}
            {order.shippingAddress.city}, {order.shippingAddress.district}<br />
            {order.shippingAddress.phone}
          </p>
        </div>

        {/* Payment & total */}
        <div className="bg-yamass-cream rounded-2xl p-5">
          <h3 className="font-semibold text-yamass-black mb-3 text-sm">Payment</h3>
          <p className="text-sm text-gray-600 capitalize mb-3">{order.paymentMethod}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>৳{order.itemsPrice.toLocaleString()}</span></div>
            <div className="flex justify-between text-gray-500"><span>Shipping</span><span>{order.shippingPrice === 0 ? 'Free' : `৳${order.shippingPrice}`}</span></div>
            <div className="flex justify-between font-bold text-yamass-black pt-2 border-t border-gray-200">
              <span>Total</span><span>৳{order.totalPrice.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Return button */}
      {canReturn && (
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-center justify-between">
          <div>
            <p className="font-medium text-blue-800 text-sm">Not satisfied with your order?</p>
            <p className="text-xs text-blue-500 mt-1">Returns accepted within 7 days of delivery</p>
          </div>
          <Link
            to={`/return/${order._id}`}
            className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-blue-700 transition-colors"
          >
            <FiRefreshCw size={14} /> Request Return
          </Link>
        </div>
      )}
    </div>
  );
}
